/**
 * Cliente HTTP minimalista pra falar com o backend Express atual.
 *
 * Em dev: chamadas a /api/* são proxyadas pelo Vite pra http://localhost:3001
 * Em prod: chamadas a /api/* vão pro mesmo host onde o app está servido
 *          (configurado via nginx — beta.igmsecurity.com.br também acessa app.igmsecurity.com.br/api)
 *
 * Pode ser substituído por React Query (já adicionado nas deps) pra cache + sync.
 */

import { reportError } from './sentry';

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public response?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Se true, não anexa Bearer token (útil pro login). */
  skipAuth?: boolean;
}

const BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

/**
 * O nome do arquivo que o servidor mandou no `Content-Disposition`. Prefere o `filename*` (RFC 5987, UTF-8 — o nome
 * com acento) e cai no `filename` (ASCII). `null` sem cabeçalho.
 */
export function nomeDoArquivoDaResposta(cabecalho: string | null): string | null {
  if (!cabecalho) return null;
  const estrela = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(cabecalho);
  if (estrela) {
    try {
      return decodeURIComponent(estrela[1].trim());
    } catch {
      /* cai no filename simples */
    }
  }
  const simples = /filename\s*=\s*"([^"]+)"|filename\s*=\s*([^;]+)/i.exec(cabecalho);
  return simples ? (simples[1] ?? simples[2]).trim() : null;
}

/** Lê token JWT do localStorage. Será trocado por estratégia melhor quando AuthContext entrar. */
export function getAuthToken(): string | null {
  return localStorage.getItem('igm_jwt');
}

export function setAuthToken(token: string | null) {
  if (token) {
    localStorage.setItem('igm_jwt', token);
  } else {
    localStorage.removeItem('igm_jwt');
  }
}

/**
 * Verifica se há JWT no localStorage. Usado pra gate `enabled` do React
 * Query — sem token, queries não disparam. Centralizado aqui pra que stores
 * não dupliquem leitura de localStorage e o audit grep fique limpo.
 */
export function hasToken(): boolean {
  return !!localStorage.getItem('igm_jwt');
}

/**
 * O10 (rodada ORCAMENTO-CONSTRUCAO) — renovação SILENCIOSA da sessão.
 *
 * Causa raiz do "logout frequente": o access token dura 1h e o front NUNCA
 * chamava `POST /auth/refresh` (o endpoint existia órfão no backend) — todo
 * 401 virava logout imediato. Agora, no primeiro 401, tentamos UMA renovação
 * com o refresh token (rotação server-side, validade 30 dias) e repetimos a
 * request original; só deslogamos se a renovação também falhar.
 *
 * Single-flight: N requests que tomam 401 ao mesmo tempo compartilham UMA
 * chamada de refresh (evita estouro de rotação — o backend revoga o token
 * antigo a cada refresh).
 */
let refreshEmVoo: Promise<boolean> | null = null;

async function tentarRefresh(): Promise<boolean> {
  if (!refreshEmVoo) {
    refreshEmVoo = (async () => {
      const refreshToken = localStorage.getItem('igm_refresh');
      if (!refreshToken) return false;
      try {
        const r = await fetch(`${BASE_URL}/api/v1/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });
        if (!r.ok) return false;
        const data = (await r.json()) as { accessToken?: string; refreshToken?: string };
        if (!data.accessToken || !data.refreshToken) return false;
        setAuthToken(data.accessToken);
        localStorage.setItem('igm_refresh', data.refreshToken);
        return true;
      } catch {
        return false;
      }
    })().finally(() => {
      // Libera pro próximo ciclo DEPOIS que todos os aguardantes leram o resultado.
      setTimeout(() => {
        refreshEmVoo = null;
      }, 0);
    });
  }
  return refreshEmVoo;
}

/**
 * A MESMA renovação silenciosa, para quem chama `fetch` direto — a fila sem sinal do aplicativo do técnico
 * (`src/lib/os-campo/campo-api.ts`), que sobe depois de horas e acha o acesso de 1 h vencido. Não desloga ninguém.
 */
export function renovarSessao(): Promise<boolean> {
  return tentarRefresh();
}

async function request<T = unknown>(
  path: string,
  options: RequestOptions = {},
  jaRenovou = false,
): Promise<T> {
  const { body, skipAuth, headers = {}, ...rest } = options;

  const finalHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(headers as Record<string, string>),
  };

  if (!skipAuth) {
    const token = getAuthToken();
    if (token) {
      finalHeaders['Authorization'] = `Bearer ${token}`;
    }
  }

  const init: RequestInit = {
    ...rest,
    headers: finalHeaders,
  };

  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, init);
  } catch (err) {
    // Network error
    reportError(err, { path, method: rest.method || 'GET' });
    throw new ApiError('Erro de rede. Verifique sua conexão.', 0);
  }

  // 401 → tenta renovar a sessão UMA vez (O10); só desloga se falhar.
  if (response.status === 401 && !skipAuth) {
    if (!jaRenovou && (await tentarRefresh())) {
      return request<T>(path, options, true);
    }
    setAuthToken(null);
    localStorage.removeItem('igm_refresh');
    // Em produção, o AuthContext vai detectar isso e mandar pro /login
    window.dispatchEvent(new CustomEvent('auth:unauthorized'));
  }

  const contentType = response.headers.get('content-type');
  const isJson = contentType?.includes('application/json');
  const data = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    const message = isJson && data?.error ? data.error : `Erro ${response.status}`;
    throw new ApiError(message, response.status, data);
  }

  return data as T;
}

export const api = {
  get: <T = unknown>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'GET' }),

  post: <T = unknown>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),

  put: <T = unknown>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', body }),

  patch: <T = unknown>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PATCH', body }),

  delete: <T = unknown>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'DELETE' }),

  /**
   * Sobe um arquivo (multipart) autenticado — a planilha de importação (mig
   * 565). ⚠️ Sem Content-Type manual: o browser define o boundary sozinho.
   */
  upload: async <T = unknown>(path: string, form: FormData): Promise<T> => {
    const headers: Record<string, string> = {};
    const token = getAuthToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    let res: Response;
    try {
      res = await fetch(`${BASE_URL}${path}`, { method: 'POST', headers, body: form });
    } catch (err) {
      reportError(err, { path, method: 'POST' });
      throw new ApiError('Erro de rede. Verifique sua conexão.', 0);
    }
    const isJson = res.headers.get('content-type')?.includes('application/json');
    const data = isJson ? await res.json() : await res.text();
    if (!res.ok) {
      const message = isJson && (data?.erro || data?.error) ? (data.erro ?? data.error) : `Erro ${res.status}`;
      throw new ApiError(message, res.status, data);
    }
    return data as T;
  },

  /**
   * Lê um arquivo binário autenticado e devolve o conteúdo (ex.: a foto da OS que a tela mostra). `<img src>` não manda o
   * Bearer; quem mostra imagem protegida a lê por aqui e usa `URL.createObjectURL`. Renova a sessão uma vez no 401.
   */
  blob: async (path: string): Promise<Blob> => {
    const pedir = () => {
      const token = getAuthToken();
      return fetch(`${BASE_URL}${path}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    };
    let res = await pedir();
    if (res.status === 401 && (await tentarRefresh())) res = await pedir();
    if (!res.ok) throw new ApiError(`Erro ${res.status}`, res.status);
    return res.blob();
  },

  /**
   * Baixa um arquivo binário autenticado (ex.: PDF) e dispara o download no
   * browser. Reusa o Bearer do `getAuthToken` (auth existente — sem storage novo).
   *
   * 18/09/2026: sem `filename`, o nome é o que o SERVIDOR mandar no `Content-Disposition` (o `filename*` UTF-8
   * primeiro, depois o `filename` ASCII) — a regra do nome mora num lugar só, o servidor. Quem passa `filename`
   * continua como estava.
   */
  download: async (path: string, filename?: string): Promise<void> => {
    const headers: Record<string, string> = {};
    const token = getAuthToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`${BASE_URL}${path}`, { headers });
    if (!res.ok) throw new ApiError(`Erro ${res.status}`, res.status);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename ?? nomeDoArquivoDaResposta(res.headers.get('content-disposition')) ?? 'arquivo';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
};
